import { useInfiniteQuery } from "@tanstack/react-query";
import { AlertTriangle, Eye, EyeOff, Globe2, Import, LayoutGrid, MapPin, Search, Sun } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, errorMessage, unwrap, type S } from "../api/client";
import { useOrgId } from "../api/org";
import { Alert, Badge, Button, Card, Empty, PageHead, Segmented, Select, Spinner } from "../components/ui";
import { date, money, num } from "../lib/format";
import { leadFiltersParams, parseLeadFilters, type LeadFilters } from "../lib/leadFilters";
import { viewState } from "../lib/quoteViews";
import { proposalView, UPDATING_LABEL, UPDATING_TOOLTIP } from "../lib/resend";
import { useT, useIntlShape } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/keys";
import { translateOutside } from "../i18n/problems";
import designsEnglish from "../i18n/locales/en/designs.json";
import type { Tr } from "../lib/tr";

type Lead = S["Lead"];

/** Outside a component that passes `t` (the Designer's list until its lane passes one): the current language's
 * catalog, else English. */
const outsideT: Tr = (key, values) => translateOutside(key, values) ?? (designsEnglish as Record<string, unknown>)[key] as string;

/** The lead's state as a badge, in words (`t` from useT()). */
export function leadState(lead: Lead, t: Tr = outsideT): { label: string; tone?: "ok" | "warn" | "danger" | "info" } {
  if (lead.job_status === "queued" || lead.job_status === "running" || lead.public_status === "processing")
    return { label: t("designs.state.designing"), tone: "info" };
  if (lead.job_status === "failed" || lead.public_status === "failed") return { label: t("designs.state.failed"), tone: "danger" };
  if (lead.status === "sold") return { label: t("designs.state.sold"), tone: "ok" };
  if (lead.public_status === "completed" || lead.quote_id) return { label: t("designs.state.quoted"), tone: "ok" };
  if (lead.public_status === "ready" || lead.kw_dc) return { label: t("designs.state.designed"), tone: "warn" };
  return { label: DESIGN_STATUS_LABELS[lead.status] ? t(DESIGN_STATUS_LABELS[lead.status]!) : lead.status };
}

export const DESIGN_STATUS_LABELS: Partial<Record<string, MessageKey>> = {
  draft: "designs.status.draft",
  proposed: "designs.status.proposed",
  sold: "designs.status.sold",
  archived: "designs.status.archived",
};

export const OFFER_LABELS: Record<S["QuoteOfferStatus"], MessageKey> = {
  draft: "designs.offer.draft",
  requested: "designs.offer.requested",
  booking_opened: "designs.offer.bookingOpened",
  updated: "designs.offer.updated",
  expired: "designs.offer.expired",
  renewal_requested: "designs.offer.renewalRequested",
};

/** An imported lead's pipeline status (as lib/migration.ts maps OpenSolar's stages). */
export const PIPELINE_STATUS_LABELS: Partial<Record<string, MessageKey>> = {
  new: "designs.pipelineStatus.new",
  contacted: "designs.pipelineStatus.contacted",
  qualified: "designs.pipelineStatus.qualified",
  unqualified: "designs.pipelineStatus.unqualified",
  won: "designs.pipelineStatus.won",
  lost: "designs.pipelineStatus.lost",
};

/** The homeowner's commitment as a chip (none while the quote is only a proposal). */
export function offerChip(status: S["QuoteOfferStatus"] | null | undefined): { label: MessageKey; tone?: "ok" | "warn" | "danger" | "info" | "grad" } | null {
  if (!status || status === "draft") return null;
  const tone = status === "expired" ? "danger" : status === "renewal_requested" ? "warn" : status === "updated" ? "info" : "grad";
  return { label: OFFER_LABELS[status], tone };
}

/** A roof report is being drawn for a recent version: the detail page polls for its "See report" button. */
export function reportPending(report: S["DesignReportState"] | null | undefined, updatedAt: string): boolean {
  return report?.status === "pending" && Date.now() - new Date(updatedAt).getTime() < 15 * 60_000;
}

/** The lead's proposal after a design save, as a chip: "Updating proposal…" (a few seconds) or the failure. */
export function ProposalChip({ proposal }: { proposal: Lead["proposal"] }) {
  const t = useT();
  const view = proposalView(proposal?.status, t);
  if (view.updating)
    return (
      <span className="badge info plain proposal-chip" title={t(UPDATING_TOOLTIP)}>
        <span className="spinner" aria-hidden="true" /> {t(UPDATING_LABEL)}
      </span>
    );
  if (view.failed)
    return (
      <span className="badge danger plain proposal-chip" title={t("designs.proposal.notUpdatedTooltip")}>
        <AlertTriangle size={12} aria-hidden="true" /> {t("designs.proposal.notUpdated")}
      </span>
    );
  return null;
}

/** The homeowner's views of the design's quote page (its project's leads): "Viewed 3× · 2 h ago" or "Not opened
 * yet"; nothing when no lead has a quote link. The tooltip adds the team's own opens (not counted). */
export function ViewChip({ views }: { views: Lead["views"] }) {
  const t = useT();
  const state = viewState(views, t);
  if (!state) return null;
  return (
    <span className={`view-chip${state.viewed ? " is-viewed" : ""}`} title={state.title}>
      {state.viewed ? <Eye size={12} aria-hidden="true" /> : <EyeOff size={12} aria-hidden="true" />}
      {state.label}
    </span>
  );
}

/** An imported lead's original pipeline stage, with our status it maps to in the tooltip. */
export function StageChip({ lead }: { lead: Pick<Lead, "stage_label" | "pipeline_status"> }) {
  const t = useT();
  if (!lead.stage_label) return null;
  const mapped = lead.pipeline_status ? PIPELINE_STATUS_LABELS[lead.pipeline_status] : undefined;
  return (
    <span
      className="badge plain info"
      title={lead.pipeline_status ? t("designs.stage.titleMapped", { status: mapped ? t(mapped) : lead.pipeline_status }) : t("designs.stage.title")}
    >
      {lead.stage_label}
    </span>
  );
}

/** Re-renders every minute, so "2 min ago" keeps up between refreshes. */
export function useMinuteTick(): void {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => setTick((n) => n + 1), 60_000);
    return () => window.clearInterval(t);
  }, []);
}

/** How often an open list looks for new quote views (React Query pauses it while the tab is hidden). */
export const VIEWS_REFRESH_MS = 30_000;

export function leadName(lead: Lead): string | null {
  const c = lead.contact;
  if (!c) return null;
  const name = [c.first_name, c.last_name].filter(Boolean).join(" ");
  return name || c.email || null;
}

function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function Designs() {
  const t = useT();
  const intl = useIntlShape();
  const orgId = useOrgId();
  const navigate = useNavigate();
  // the search and filters live in the address: Back from a lead, a reload or a shared link keep them (lib/leadFilters)
  const [params, setParams] = useSearchParams();
  const { q, status, source, offer, views: viewed, stage } = parseLeadFilters(params);
  const update = (patch: Partial<LeadFilters>) =>
    setParams((current) => leadFiltersParams({ ...parseLeadFilters(current), ...patch }, current), { replace: true });
  const setQ = (value: string) => update({ q: value });
  const setStatus = (value: LeadFilters["status"]) => update({ status: value });
  const setSource = (value: LeadFilters["source"]) => update({ source: value });
  const setOffer = (value: LeadFilters["offer"]) => update({ offer: value });
  const setViewed = (value: LeadFilters["views"]) => update({ views: value });
  const setStage = (value: string) => update({ stage: value });
  const query = useDebounced(q.trim());
  const stageQuery = useDebounced(stage.trim());
  useMinuteTick();

  const leads = useInfiniteQuery({
    queryKey: ["leads-infinite", orgId, query, status, source, offer, viewed, stageQuery],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      unwrap(
        api.GET("/v1/orgs/{org_id}/leads", {
          params: {
            path: { org_id: orgId },
            query: {
              limit: 25,
              cursor: pageParam,
              q: query || undefined,
              status: status || undefined,
              source: source === "all" ? undefined : source,
              offer_status: offer || undefined,
              views: viewed || undefined,
              stage: source === "import" && stageQuery ? stageQuery : undefined,
            },
          },
        }),
      ),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    placeholderData: (prev) => prev,
    refetchInterval: (q) =>
      q.state.data?.pages.some((p) => p.items.some((i) => i.proposal?.status === "updating"))
        ? 3000
        : q.state.data?.pages.some((p) => p.items.some((i) => i.job_status === "queued" || i.job_status === "running"))
          ? 5000
          : VIEWS_REFRESH_MS, // a homeowner opening their quote shows up without a reload
  });
  const items = leads.data?.pages.flatMap((p) => p.items) ?? [];
  const filtered = !!(query || status || offer || viewed || source !== "all");
  const stageLabels = [...new Set(items.map((i) => i.stage_label).filter((x): x is string => !!x))];

  return (
    <>
      <PageHead
        title={t("shell.nav.designsLeads")}
        description={t("designs.list.description")}
      />
      <Card pad={false}>
        <div className="toolbar">
          <div className="input-icon">
            <Search />
            <input className="input" placeholder={t("designs.list.search")} aria-label={t("designs.list.search")} value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <Segmented
            value={source}
            onChange={setSource}
            options={[
              { value: "all", label: t("designs.source.all") },
              { value: "website", label: t("designs.source.website") },
              { value: "dashboard", label: t("designs.source.dashboard") },
              { value: "import", label: t("designs.source.import") },
            ]}
          />
          {source === "import" && (
            <div style={{ width: 190 }}>
              <input className="input" list="stage-labels" aria-label={t("designs.stage.title")} placeholder={t("designs.stage.title")} value={stage} onChange={(e) => setStage(e.target.value)} />
              <datalist id="stage-labels">
                {stageLabels.map((l) => (
                  <option key={l} value={l} />
                ))}
              </datalist>
            </div>
          )}
          <div style={{ width: 170 }}>
            <Select
              value={offer}
              onChange={setOffer}
              ariaLabel={t("designs.filter.quote")}
              options={[
                { value: "", label: t("designs.filter.anyQuote") },
                ...(["requested", "booking_opened", "updated", "expired", "renewal_requested", "draft"] as const).map((v) => ({ value: v, label: t(OFFER_LABELS[v]) })),
              ]}
            />
          </div>
          <div style={{ width: 160 }}>
            <Select
              value={viewed}
              onChange={setViewed}
              ariaLabel={t("designs.filter.views")}
              options={[
                { value: "", label: t("designs.filter.anyViews") },
                { value: "viewed", label: t("designs.filter.viewed") },
                { value: "not_viewed", label: t("designs.views.notOpened") },
              ]}
            />
          </div>
          <div style={{ width: 150 }}>
            <Select
              value={status}
              onChange={setStatus}
              ariaLabel={t("designs.filter.status")}
              options={[
                { value: "", label: t("designs.filter.anyStatus") },
                ...(["draft", "proposed", "sold", "archived"] as const).map((v) => ({ value: v, label: t(DESIGN_STATUS_LABELS[v]!) })),
              ]}
            />
          </div>
        </div>
        {leads.isLoading ? (
          <Spinner />
        ) : leads.error ? (
          <div className="card-body">
            <Alert tone="danger">{errorMessage(leads.error)}</Alert>
          </div>
        ) : !items.length ? (
          filtered ? (
            <Empty icon={<Search />} title={t("designs.list.noMatches")}>
              {t("designs.list.noMatchesBody")}
            </Empty>
          ) : (
            <Empty icon={<LayoutGrid />} title={t("designs.list.empty")} action={<Link className="btn btn-primary" to="/get-started">{t("auth.getStarted.step2.title")}</Link>}>
              {t("designs.list.emptyBody")}
            </Empty>
          )
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t("designs.column.site")}</th>
                  <th className="hide-sm">{t("designs.column.lead")}</th>
                  <th className="hide-sm">{t("designs.column.status")}</th>
                  <th className="num hide-sm">{t("designs.column.system")}</th>
                  <th className="num hide-sm">{t("designs.column.price")}</th>
                  <th className="num hide-sm">{t("designs.column.date")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((lead) => {
                  const state = leadState(lead, t);
                  const name = leadName(lead);
                  const chip = offerChip(lead.offer_status);
                  return (
                    <tr key={lead.design_id} className="clickable" onClick={() => navigate(`/designs/${lead.design_id}`)}>
                      <td style={{ maxWidth: 360 }}>
                        <div className="row" style={{ gap: 10, flexWrap: "nowrap" }}>
                          <div className="empty-icon" style={{ width: 34, height: 34, borderRadius: 10, display: "grid", placeItems: "center", background: "var(--grad-soft)", color: "var(--primary)", flex: "none" }}>
                            {lead.source === "website" ? <Globe2 size={16} /> : lead.source === "import" ? <Import size={16} /> : <MapPin size={16} />}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div className="cell-main ellipsis">
                              <Link to={`/designs/${lead.design_id}`} onClick={(e) => e.stopPropagation()} style={{ color: "inherit" }} dir="auto">
                                {lead.address || lead.design_name}
                              </Link>
                            </div>
                            <div className="cell-sub ellipsis">
                              {t(lead.source === "website" ? "designs.origin.website" : lead.source === "import" ? "designs.origin.import" : "designs.origin.dashboard")} · {name ?? lead.design_name}
                            </div>
                            <div className="show-sm row" style={{ gap: 8, marginTop: 6 }}>
                              <Badge tone={state.tone}>{state.label}</Badge>
                              <ProposalChip proposal={lead.proposal} />
                              <ViewChip views={lead.views} />
                              {lead.kw_dc ? <span className="small muted">{t("designs.list.sizePanels", { kw: num(lead.kw_dc, 1), count: lead.panel_count ?? 0 })}</span> : null}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="hide-sm" style={{ maxWidth: 220 }}>
                        {name ? (
                          <>
                            <div className="cell-main ellipsis">{name}</div>
                            <div className="cell-sub ellipsis" dir="auto">{lead.contact?.email || lead.contact?.phone || ""}</div>
                          </>
                        ) : (
                          !lead.views && <span className="faint">—</span>
                        )}
                        {lead.views && (
                          <div className="lead-views">
                            <ViewChip views={lead.views} />
                          </div>
                        )}
                      </td>
                      <td className="hide-sm">
                        <div className="row" style={{ gap: 6 }}>
                          <Badge tone={state.tone}>{state.label}</Badge>
                          {chip && <Badge tone={chip.tone}>{t(chip.label)}</Badge>}
                          <ProposalChip proposal={lead.proposal} />
                          <StageChip lead={lead} />
                        </div>
                        {lead.quote_number && (
                          <div className="cell-sub mono" style={{ marginTop: 4 }}>
                            <span dir="ltr">{lead.quote_number}</span>
                            {lead.valid_until ? ` · ${t("designs.list.validUntil", { date: date(lead.valid_until) })}` : ""}
                          </div>
                        )}
                      </td>
                      <td className="num hide-sm">
                        {lead.kw_dc ? (
                          <>
                            <div className="cell-main">{num(lead.kw_dc, 1)} kW</div>
                            <div className="cell-sub">
                              <Sun size={11} style={{ verticalAlign: -1 }} /> {t("designs.list.panels", { count: lead.panel_count ?? 0 })}
                            </div>
                          </>
                        ) : (
                          <span className="faint">—</span>
                        )}
                      </td>
                      <td className="num hide-sm">
                        {lead.quote_total != null ? (
                          <>
                            <div className="cell-main">{money(lead.quote_total, lead.currency ?? "USD")}</div>
                            <div className="cell-sub">{t("designs.list.net", { amount: money(lead.quote_net_cost, lead.currency ?? "USD") })}</div>
                          </>
                        ) : (
                          <span className="faint">—</span>
                        )}
                      </td>
                      <td className="num hide-sm">
                        <div>{date(lead.created_at)}</div>
                        <div className="cell-sub">{intl.formatTime(lead.created_at, { hour: "2-digit", minute: "2-digit" })}</div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {leads.hasNextPage && (
          <div className="card-foot" style={{ justifyContent: "center" }}>
            <Button loading={leads.isFetchingNextPage} onClick={() => leads.fetchNextPage()}>
              {t("designs.list.loadMore")}
            </Button>
          </div>
        )}
      </Card>
    </>
  );
}
